import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Shield, Building2, FileText, Landmark, Scale, HelpCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { publicApi } from '../lib/api';
import { useLanguage } from '../hooks/useLanguage';
import { useCMSContent } from '../hooks/useCMSContent';
import SEO from '../components/SEO';

// Icon mapping
const iconMap = {
  Shield,
  Building2,
  FileText,
  Landmark,
  Scale,
  HelpCircle,
};

const PricingPage = () => {
  const { t, language } = useLanguage();
  const { blocks } = useCMSContent('pricing');
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  // Helper to get CMS content with language support, falling back to translations
  const getCMSText = useCallback((blockKey, field, translationKey) => {
    const block = blocks[blockKey];
    if (block && Object.keys(block).length > 0) {
      const langField = language === 'es' ? `${field}_es` : field;
      return block[langField] || block[field] || t(translationKey);
    }
    return t(translationKey);
  }, [blocks, language, t]);

  // Fetch pricing packages from backend
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await publicApi.getPricingPackages();
        setPackages(response.data || []);
      } catch (error) {
        console.error('Failed to fetch pricing packages:', error);
        setPackages([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPackages();
    publicApi.trackEvent({ event_type: 'page_view', page: 'pricing' }).catch(() => {});
  }, []);

  const processSteps = useMemo(() => ['step01', 'step02', 'step03', 'step04'], []);

  // Get localized package data
  const getPackageData = (pkg) => {
    if (language === 'es') {
      return {
        name: pkg.name_es || pkg.name,
        price: pkg.price_es || pkg.price,
        priceNote: pkg.price_note_es || pkg.price_note,
        description: pkg.description_es || pkg.description,
        features: pkg.features_es || pkg.features || [],
      };
    }
    return {
      name: pkg.name,
      price: pkg.price,
      priceNote: pkg.price_note,
      description: pkg.description,
      features: pkg.features || [],
    };
  };

  return (
    <div data-testid="pricing-page">
      <SEO 
        title="Legal Services Pricing"
        description="Transparent, flat-fee pricing for trademark registration, business formation, and contract services. No surprise bills or hourly uncertainty."
      />
      {/* Hero */}
      <section className="bg-[#FDFCFA] border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D] mb-6">
              {getCMSText('hero', 'badge', 'pricing.sectionLabel')}
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif tracking-tight leading-[1.1] text-[#222222] mb-6">
              {getCMSText('hero', 'headline', 'pricing.headline')}
            </h1>
            <p className="text-lg text-[#555555] leading-relaxed mb-8">
              {getCMSText('hero', 'description', 'pricing.description')}
            </p>
            <Link to="/book" data-testid="pricing-hero-cta">
              <Button className="bg-[#1F2A44] hover:bg-[#151D30] text-white px-8 py-4 rounded-sm text-sm font-medium tracking-wide uppercase">
                {t('nav.bookConsultation')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing Grid */}
      <section className="bg-white border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#1F2A44]" />
            </div>
          ) : packages.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-[#555555]">No pricing packages available.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {packages.map((pkg, index) => {
                const Icon = iconMap[pkg.icon] || Shield;
                const pkgData = getPackageData(pkg);
                return (
                  <div
                    key={pkg.id || index}
                    className={`relative p-8 border ${
                      pkg.popular 
                        ? 'border-[#1F2A44] bg-[#FDFCFA]' 
                        : 'border-[#E8E4DC] bg-white'
                    }`}
                    data-testid={`pricing-card-${index}`}
                  >
                    {pkg.popular && (
                      <div className="absolute -top-3 left-8 bg-[#1F2A44] text-white text-xs font-mono uppercase tracking-wider px-3 py-1">
                        {t('pricing.popular')}
                      </div>
                    )}
                    <Icon className="w-8 h-8 text-[#1F2A44] mb-4" />
                    <h3 className="text-xl font-serif text-[#222222] mb-2">{pkgData.name}</h3>
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-3xl font-serif text-[#1F2A44]">{pkgData.price}</span>
                      {pkgData.priceNote && (
                        <span className="text-sm text-[#555555]">{pkgData.priceNote}</span>
                      )}
                    </div>
                    <p className="text-sm text-[#555555] mb-6">{pkgData.description}</p>
                    <ul className="space-y-3 mb-8">
                      {Array.isArray(pkgData.features) && pkgData.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-[#1F2A44] mt-0.5 flex-shrink-0" />
                          <span className="text-[#555555]">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to={pkg.checkout_url || "/book"}>
                      <Button
                        variant={pkg.popular ? 'default' : 'outline'}
                        className={`w-full rounded-sm ${
                          pkg.popular
                            ? 'bg-[#1F2A44] hover:bg-[#151D30] text-white'
                            : 'border-[#1F2A44] text-[#1F2A44] hover:bg-[#1F2A44] hover:text-white'
                        }`}
                        data-testid={`get-started-btn-${pkg.slug}`}
                      >
                        {t('pricing.getStarted')}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Process */}
      <section className="bg-[#1F2A44]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="text-center mb-12">
            <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D] mb-4">
              {t('pricing.process.label')}
            </p>
            <h2 className="text-3xl md:text-4xl font-serif text-white">
              {t('pricing.process.headline')}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => {
              const stepData = t(`pricing.process.${step}`);
              return (
                <div key={step} className="text-center">
                  <div className="w-12 h-12 bg-[#C2A46D] text-[#1F2A44] text-xl font-bold flex items-center justify-center mx-auto mb-4">
                    {index + 1}
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{stepData.title}</h3>
                  <p className="text-sm text-white/70">{stepData.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#FDFCFA]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="text-center mb-12">
            <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D] mb-4">
              {t('pricing.faq.label')}
            </p>
            <h2 className="text-3xl md:text-4xl font-serif text-[#222222]">
              {t('pricing.faq.headline')}
            </h2>
          </div>
          <div className="space-y-6">
            {['q1', 'q2', 'q3', 'q4'].map((key) => {
              const faq = t(`pricing.faq.${key}`);
              return (
                <div key={key} className="border-b border-[#E8E4DC] pb-6">
                  <h3 className="text-lg font-semibold text-[#222222] mb-2">{faq.question}</h3>
                  <p className="text-[#555555]">{faq.answer}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1F2A44]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 text-center">
          <h2 className="text-3xl md:text-4xl font-serif text-white mb-6">
            {t('cta.headline')}
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            {t('cta.subheadline')}
          </p>
          <Link to="/book">
            <Button className="bg-white text-[#1F2A44] hover:bg-[#FDFCFA] px-10 py-4 rounded-sm text-sm font-medium tracking-wide uppercase">
              {t('nav.bookConsultation')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
